import { Suspense, useEffect, useState } from 'react';
import {
  getBorderProps,
  getFontSize,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../../utils/helper';
import { PaddingType } from '../../../puck/reusable-props/padding';
import { IBorderValueProps } from '../../../puck/reusable-props/Border';
import { MarginType } from '../../../puck/reusable-props/NewMargin';
import { ITextFieldOptions } from '../../../interface/formInterface';
import { WidthAndHeightSubProps } from '../../../puck/reusable-props/widthAndHeight';
import {
  ICurrentUpsellData,
  IPostUpsellVariant,
  ISelectedUpsellItems,
} from '../../../interface/postUpsellInterface';
import { checkAndReturnWithProductVariantIdPrefix } from '../../../utils/constants';
import LoadingSpinner from '../../shared/LoadingSpinner';
import { WidthProps } from '../../../puck/reusable-props/width';
import { getCurrencyWithSymbol } from '../../../utils/formatPrice';
import PostUpsellPayPal from '../PostUpsell-PayPal';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import { Shop } from '../../../interface/storeInterface';

interface EachProductCardProps extends ReduxDispatchProps, ReduxSelectorProps {
  product: {
    id: string;
    variants: {
      productPrice: string;
      shippingType: {
        label: string;
        value: string;
      };
      shippingPrice: string;
      discountAmount: {
        type: string;
        value: string;
      };
      productVariants: IPostUpsellVariant;
      totalUpSellPrice: string;
      displayPrice: string;
    }[];
    assignedProducts: {
      label: string;
      value: number;
    };
  };
  currentDeviceType: string;
  cardCustomization: {
    backgroundColor: {
      color: string;
      isInherit: boolean;
    };
    cardPosition: string;
    Border: IBorderValueProps;
    hideShadow: boolean;
  };
  imageCustomization: {
    Border: IBorderValueProps;
    hideImage: boolean;
    imageSize: WidthAndHeightSubProps;
  };
  productName: ITextFieldOptions;
  productPrice: ITextFieldOptions;
  productVariantStyle: ITextFieldOptions;
  orderDetailsCustomization: {
    subTotalText: string;
    grandTotalText: string;
    discountText: string;
    shippingText: string;
    hideSubTotal: boolean;
    hideShipping: boolean;
    hideDiscount: boolean;

    hideGrandTotal: boolean;
    color: string;
    Padding: PaddingType;
  };
  buttonCustomization: {
    buttonText: string;
    removeButtonText: string;
    Padding: PaddingType;
    Border: IBorderValueProps;
    Margin: MarginType;
    hideButton: boolean;
    buttonPosition: string;
    hidePrice: boolean;
    priceColor: string;
    priceFontSize: string;
    priceFontWeight: string;
    backgroundColor: {
      color: string;
      isInherit: boolean;
    };
  } & WidthProps;
  declineButtonCustomization: {
    buttonText: string;
    Padding: PaddingType;
    Border: IBorderValueProps;
    Margin: MarginType;
    showButton: boolean;
    buttonPosition: string;
    backgroundColor: {
      color: string;
      isInherit: boolean;
    };
  };
  isLoading: boolean;
  handleClick: (
    _variantValue: string,
    _isSelected: boolean,
    _id: string,
  ) => void;
  handleDeclineClick: () => void;
  stayOnPage: boolean;
  selectedUpsellItems: ISelectedUpsellItems[];
  isFunnelPreview?: boolean;
  isCheckoutPaymentPaypal?: boolean;
  handleClickPayPal?: () => Promise<void>;
  purchaseToken?: string;
  checkoutOrderID?: string;
  IndependentTransactionForUpsellPages?: boolean;
  storeData: Shop;
  currentUpsellData: ICurrentUpsellData;
  isNavToThankyouPage?: boolean;
  navigateFunctionForConfirmationPage: () => void;
  smartUpsellFunnelID?: string;
}

export const EachProductCard = ({
  product,
  currentDeviceType,
  cardCustomization,
  imageCustomization,
  productName,
  productPrice,
  productVariantStyle,
  orderDetailsCustomization,
  buttonCustomization,
  declineButtonCustomization,
  isLoading,
  handleClick,
  handleDeclineClick,
  stayOnPage,
  selectedUpsellItems,
  isFunnelPreview = false,
  isCheckoutPaymentPaypal,
  handleClickPayPal,
  purchaseToken = '',
  currentUpsellData,
  storeData,
  isNavToThankyouPage = false,
  navigateFunctionForConfirmationPage,
  smartUpsellFunnelID,
  useAppDispatch,
}: EachProductCardProps) => {
  const variants = product?.variants || [];
  const [selectedVariant, setSelectedVariant] = useState(variants[0] || {});
  const isSelected = stayOnPage
    ? selectedUpsellItems?.some(
        item =>
          item.variantId ===
          checkAndReturnWithProductVariantIdPrefix(
            '' + (selectedVariant?.productVariants?.value || ''),
          ),
      )
    : false;

  const handleVariantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    const newSelectedVariant =
      variants.find(
        variant =>
          String(variant?.productVariants?.value) === String(selectedValue),
      ) ||
      variants[0] ||
      {};
    setSelectedVariant(newSelectedVariant);
  };

  const buttonText =
    stayOnPage && isSelected
      ? buttonCustomization.removeButtonText
      : buttonCustomization.buttonText; /// need to change

  const discountAmount = Number(
    selectedVariant?.discountAmount?.type === 'amount'
      ? selectedVariant?.discountAmount?.value
      : (Number(selectedVariant.productVariants.productPrice) *
          Number(selectedVariant?.discountAmount?.value)) /
          100,
  );

  const totalPrice = (
    Number(selectedVariant.productVariants.productPrice) +
    Number(selectedVariant?.shippingPrice) -
    discountAmount
  ).toFixed(2); // grand total price after discount and shipping

  const discountedPrice = (
    Number(selectedVariant.productVariants.productPrice) - discountAmount
  ).toFixed(2); // product price after discount

  useEffect(() => {
    if (
      isFunnelPreview &&
      variants.length > 0 &&
      !selectedUpsellItems?.length
    ) {
      setSelectedVariant(variants[0]);
    }
  }, [variants]);

  return (
    <div
      key={product.assignedProducts?.value || selectedVariant.productPrice}
      className={`flex flex-col w-full p-4 bg-white ${!cardCustomization?.hideShadow ? 'shadow-md' : ''} justify-between ${cardCustomization?.Border?.borderStyle?.[currentDeviceType]}`}
      style={{
        ...getBorderProps(cardCustomization?.Border, currentDeviceType),
        backgroundColor: cardCustomization?.backgroundColor?.isInherit
          ? 'inherit'
          : cardCustomization?.backgroundColor?.color,
      }}>
      <div
        className={`flex ${cardCustomization?.cardPosition === 'col' ? 'flex-col items-center justify-between h-full' : 'flex-row items-start gap-4'}`}>
        {!imageCustomization?.hideImage && (
          <img
            style={{
              ...(imageCustomization?.Border && currentDeviceType
                ? getBorderProps(imageCustomization?.Border, currentDeviceType)
                : {}),
              ...(currentDeviceType &&
              imageCustomization?.imageSize?.maxWidth &&
              imageCustomization?.imageSize?.height &&
              imageCustomization?.imageSize?.maxWidth[currentDeviceType] !==
                undefined &&
              imageCustomization?.imageSize?.height[currentDeviceType] !==
                undefined
                ? {
                    maxWidth: isNaN(
                      +imageCustomization?.imageSize.maxWidth[
                        currentDeviceType
                      ],
                    )
                      ? imageCustomization?.imageSize.maxWidth[
                          currentDeviceType
                        ]
                      : `${+imageCustomization?.imageSize.maxWidth[currentDeviceType]}px`,
                    height: isNaN(
                      +imageCustomization?.imageSize.height[currentDeviceType],
                    )
                      ? imageCustomization?.imageSize.height[currentDeviceType]
                      : `${+imageCustomization?.imageSize.height[currentDeviceType]}px`,
                  }
                : {}),
            }}
            className={`w-1/3 ${imageCustomization?.Border?.borderStyle?.[currentDeviceType]}`}
            src={selectedVariant?.productVariants?.productImage}
            alt={product?.assignedProducts?.label || 'Product-Image'}
          />
        )}
        <div className="w-2/3 flex flex-col">
          <h3
            className="text-lg font-bold"
            style={{
              color: productName?.color,
              fontSize: getFontSize(productName?.fontSize),
              fontWeight: productName?.fontWeight,
              fontFamily: productName?.fontFamily,
              backgroundColor: productName?.textBackgroundColor?.isInherit
                ? 'inherit'
                : productName?.textBackgroundColor?.color,
              ...getPaddingStyles(productName?.padding, currentDeviceType),
            }}>
            {product?.assignedProducts?.label || 'Default Product'}
          </h3>
          <p
            className="text-gray-500 mt-1"
            style={{
              color: productPrice?.color,
              fontSize: getFontSize(productPrice?.fontSize),
              fontWeight: productPrice?.fontWeight,
              fontFamily: productPrice?.fontFamily,
              backgroundColor: productPrice?.textBackgroundColor?.isInherit
                ? 'inherit'
                : productPrice?.textBackgroundColor?.color,
              ...getPaddingStyles(productPrice?.padding, currentDeviceType),
            }}>
            {discountAmount != 0 && (
              <>
                <s>
                  {getCurrencyWithSymbol(
                    selectedVariant?.productVariants?.currencyCode || 'USD',
                    selectedVariant?.productVariants?.productPrice,
                  )}
                </s>{' '}
              </>
            )}
            {getCurrencyWithSymbol(
              selectedVariant?.productVariants?.currencyCode || 'USD',
              discountedPrice,
            )}
          </p>

          {(!productVariantStyle?.hideFileds || false) && (
            <select
              style={{
                backgroundColor: productVariantStyle?.textBackgroundColor
                  ?.isInherit
                  ? 'inherit'
                  : productVariantStyle?.textBackgroundColor?.color,
                color: productVariantStyle?.color,
                fontSize: getFontSize(productVariantStyle?.fontSize),
                fontWeight: productVariantStyle?.fontWeight,
                fontFamily: productVariantStyle?.fontFamily,
                ...getPaddingStyles(
                  productVariantStyle?.padding,
                  currentDeviceType,
                ),
              }}
              className="mt-2 p-1 border rounded w-full"
              value={selectedVariant.productVariants?.value || ''}
              onChange={handleVariantChange}>
              {variants.map(variant => (
                <option
                  key={variant.productVariants.value}
                  value={variant.productVariants.value}>
                  <span
                    style={{
                      backgroundColor: productVariantStyle?.textBackgroundColor
                        ?.isInherit
                        ? 'inherit'
                        : productVariantStyle?.textBackgroundColor?.color,
                      color: productVariantStyle?.color,
                    }}>
                    {variant.productVariants.label}
                  </span>
                </option>
              ))}
            </select>
          )}

          {!orderDetailsCustomization?.hideSubTotal && (
            <>
              <hr className="mb-2 border-t border-gray-300 mt-2" />
              <div
                className={`flex items-center justify-between `}
                style={{
                  ...getPaddingStyles(
                    orderDetailsCustomization?.Padding,
                    currentDeviceType,
                  ),
                }}>
                <dt
                  dangerouslySetInnerHTML={{
                    __html: orderDetailsCustomization?.subTotalText,
                  }}
                />
                <dd
                  style={{
                    color: orderDetailsCustomization?.color,
                  }}>
                  {selectedVariant.displayPrice ||
                    selectedVariant.productVariants.productPrice}
                </dd>
              </div>
            </>
          )}

          {!orderDetailsCustomization?.hideDiscount && discountAmount != 0 && (
            <>
              <hr className="mb-2 border-t border-gray-300 mt-2" />
              <div
                className={`flex items-center justify-between `}
                style={{
                  ...getPaddingStyles(
                    orderDetailsCustomization?.Padding,
                    currentDeviceType,
                  ),
                }}>
                <dt
                  dangerouslySetInnerHTML={{
                    __html: orderDetailsCustomization?.discountText,
                  }}
                />
                <dd
                  style={{
                    color: orderDetailsCustomization?.color,
                  }}>
                  -{discountAmount.toFixed(2)}
                </dd>
              </div>
            </>
          )}

          {!orderDetailsCustomization?.hideShipping && (
            <>
              <hr className="mb-2 border-t border-gray-300 mt-2" />
              <div
                className={`flex items-center justify-between `}
                style={{
                  ...getPaddingStyles(
                    orderDetailsCustomization?.Padding,
                    currentDeviceType,
                  ),
                }}>
                <dt
                  dangerouslySetInnerHTML={{
                    __html: orderDetailsCustomization?.shippingText,
                  }}
                />
                <dd
                  style={{
                    color: orderDetailsCustomization?.color,
                  }}>
                  {Number(selectedVariant?.shippingPrice) == 0
                    ? 'Free'
                    : selectedVariant?.shippingPrice}
                </dd>
              </div>
            </>
          )}

          {!orderDetailsCustomization?.hideGrandTotal && (
            <>
              <hr className="mt-2 border-t border-gray-300" />
              <div className={`flex items-center justify-between `}>
                <dt
                  dangerouslySetInnerHTML={{
                    __html: orderDetailsCustomization?.grandTotalText,
                  }}
                />
                <dd
                  style={{
                    color: orderDetailsCustomization?.color,
                  }}>
                  {totalPrice}
                </dd>
              </div>
            </>
          )}
          {buttonCustomization?.buttonPosition === 'right' &&
            !buttonCustomization?.hideButton && (
              <div className="relative">
                {!isCheckoutPaymentPaypal || stayOnPage ? (
                  <button
                    className={`mt-7 ${buttonCustomization?.width || 'w-full'} bg-black text-white py-2 rounded flex justify-self-center ${buttonCustomization?.Border?.borderStyle?.[currentDeviceType]}`}
                    style={{
                      ...getNewMarginStyles(
                        buttonCustomization?.Margin,
                        currentDeviceType,
                      ),
                      ...getBorderProps(
                        buttonCustomization?.Border,
                        currentDeviceType,
                      ),
                      ...getPaddingStyles(
                        buttonCustomization?.Padding,
                        currentDeviceType,
                      ),
                      backgroundColor: buttonCustomization?.backgroundColor
                        ?.isInherit
                        ? 'inherit'
                        : buttonCustomization?.backgroundColor?.color,
                    }}
                    disabled={isLoading}
                    onClick={() =>
                      handleClick(
                        selectedVariant.productVariants.value || '',
                        isSelected,
                        product.id,
                      )
                    }>
                    <div className={`flex mx-auto`}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: buttonText,
                        }}
                      />
                      {!isSelected && !buttonCustomization?.hidePrice && (
                        <div
                          className={`${buttonCustomization?.priceFontSize} ${buttonCustomization?.priceFontWeight} pl-1`}
                          style={{ color: buttonCustomization?.priceColor }}>
                          {getCurrencyWithSymbol(
                            selectedVariant?.productVariants?.currencyCode ||
                              'USD',
                            totalPrice,
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                ) : (
                  <Suspense
                    fallback={
                      <button className="px-3 py-1.5 rounded-md bg-gray-400 text-white text-sm animate-pulse w-32 h-10" />
                    }>
                    <div
                      className={`${buttonCustomization?.width || 'w-full'} mx-auto`}>
                      <PostUpsellPayPal
                        selectedItems={[
                          {
                            variantId: checkAndReturnWithProductVariantIdPrefix(
                              '' + selectedVariant.productVariants.value,
                            ),
                            GroupId: currentUpsellData?.upsellgroup_id || '',
                            IsDownsell: currentUpsellData?.isDownSell,
                            DownsellId: currentUpsellData?.isDownSell
                              ? currentUpsellData?.downsell_id
                              : '',
                            ProductId: product.id || '',
                          },
                        ]}
                        purchaseToken={purchaseToken}
                        buttonSize={{ height: '100%' }}
                        handleClick={handleClickPayPal || (async () => {})}
                        storeData={storeData}
                        navigateToConfirmationPage={
                          navigateFunctionForConfirmationPage
                        }
                        smartUpsellFunnelID={smartUpsellFunnelID || ''}
                        useAppDispatch={useAppDispatch}
                        isNavToThankyouPage={isNavToThankyouPage}
                      />
                    </div>
                  </Suspense>
                )}
                {isLoading && stayOnPage && (
                  <div className="absolute inset-0 cursor-not-allowed flex justify-center items-center bg-white bg-opacity-90">
                    <div className="h-9">
                      <LoadingSpinner />
                    </div>
                  </div>
                )}
              </div>
            )}
          {declineButtonCustomization?.buttonPosition === 'right' &&
            declineButtonCustomization?.showButton && (
              <div className="relative">
                <button
                  className={`mt-7 w-full bg-black text-white py-2 rounded ${declineButtonCustomization?.Border?.borderStyle?.[currentDeviceType]}`}
                  style={{
                    ...getNewMarginStyles(
                      declineButtonCustomization?.Margin,
                      currentDeviceType,
                    ),
                    ...getBorderProps(
                      declineButtonCustomization?.Border,
                      currentDeviceType,
                    ),
                    ...getPaddingStyles(
                      declineButtonCustomization?.Padding,
                      currentDeviceType,
                    ),
                    backgroundColor: declineButtonCustomization?.backgroundColor
                      ?.isInherit
                      ? 'inherit'
                      : declineButtonCustomization?.backgroundColor?.color,
                  }}
                  disabled={isLoading}
                  onClick={() => handleDeclineClick()}>
                  <dt
                    dangerouslySetInnerHTML={{
                      __html: declineButtonCustomization.buttonText,
                    }}
                  />
                </button>
                {isLoading && stayOnPage && (
                  <div className="absolute inset-0 cursor-not-allowed flex justify-center items-center bg-white bg-opacity-90">
                    <div className="h-9">
                      <LoadingSpinner />
                    </div>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
      {buttonCustomization?.buttonPosition === 'bottom' &&
        !buttonCustomization?.hideButton && (
          <div className="relative">
            {!isCheckoutPaymentPaypal || stayOnPage ? (
              <button
                className={`mt-4 ${buttonCustomization?.width || 'w-full'} bg-black text-white py-2 rounded flex justify-self-center ${buttonCustomization?.Border?.borderStyle?.[currentDeviceType]}`}
                disabled={isLoading}
                style={{
                  ...getNewMarginStyles(
                    buttonCustomization?.Margin,
                    currentDeviceType,
                  ),
                  ...getBorderProps(
                    buttonCustomization?.Border,
                    currentDeviceType,
                  ),
                  ...getPaddingStyles(
                    buttonCustomization?.Padding,
                    currentDeviceType,
                  ),
                  backgroundColor: buttonCustomization?.backgroundColor
                    ?.isInherit
                    ? 'inherit'
                    : buttonCustomization?.backgroundColor?.color,
                }}
                onClick={() =>
                  handleClick(
                    selectedVariant.productVariants.value || '',
                    isSelected,
                    product.id,
                  )
                }>
                <div className={`flex mx-auto`}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: buttonText,
                    }}
                  />
                  {!isSelected && !buttonCustomization?.hidePrice && (
                    <div
                      className={`${buttonCustomization?.priceFontSize} ${buttonCustomization?.priceFontWeight} pl-1`}
                      style={{ color: buttonCustomization?.priceColor }}>
                      {' '}
                      {getCurrencyWithSymbol(
                        selectedVariant?.productVariants?.currencyCode || 'USD',
                        totalPrice,
                      )}
                    </div>
                  )}
                </div>
              </button>
            ) : (
              <Suspense
                fallback={
                  <button className="px-3 py-1.5 rounded-md bg-gray-400 text-white text-sm animate-pulse w-32 h-10" />
                }>
                <div
                  className={`${buttonCustomization?.width || 'w-full'} mx-auto`}>
                  <PostUpsellPayPal
                    selectedItems={[
                      {
                        variantId: checkAndReturnWithProductVariantIdPrefix(
                          '' + selectedVariant.productVariants.value,
                        ),
                        GroupId: currentUpsellData?.upsellgroup_id || '',
                        IsDownsell: currentUpsellData?.isDownSell,
                        DownsellId: currentUpsellData?.isDownSell
                          ? currentUpsellData?.downsell_id
                          : '',
                        ProductId: product.id || '',
                      },
                    ]}
                    purchaseToken={purchaseToken}
                    buttonSize={{ height: '100%' }}
                    handleClick={handleClickPayPal || (async () => {})}
                    storeData={storeData}
                    navigateToConfirmationPage={
                      navigateFunctionForConfirmationPage
                    }
                    smartUpsellFunnelID={smartUpsellFunnelID || ''}
                    useAppDispatch={useAppDispatch}
                    isNavToThankyouPage={isNavToThankyouPage}
                  />
                </div>
              </Suspense>
            )}
            {isLoading && !stayOnPage && (
              <div className="absolute inset-0 cursor-not-allowed flex justify-center items-center bg-white bg-opacity-90">
                <div className="h-9">
                  <LoadingSpinner />
                </div>
              </div>
            )}
          </div>
        )}
      {declineButtonCustomization?.buttonPosition === 'bottom' &&
        declineButtonCustomization?.showButton && (
          <div className="relative">
            <button
              className={`mt-4 w-full bg-black text-white py-2 rounded ${declineButtonCustomization?.Border?.borderStyle?.[currentDeviceType]}`}
              disabled={isLoading}
              style={{
                ...getNewMarginStyles(
                  declineButtonCustomization?.Margin,
                  currentDeviceType,
                ),
                ...getBorderProps(
                  declineButtonCustomization?.Border,
                  currentDeviceType,
                ),
                ...getPaddingStyles(
                  declineButtonCustomization?.Padding,
                  currentDeviceType,
                ),
                backgroundColor: declineButtonCustomization?.backgroundColor
                  ?.isInherit
                  ? 'inherit'
                  : declineButtonCustomization?.backgroundColor?.color,
              }}
              onClick={() => handleDeclineClick()}>
              <div className={`flex justify-center`}>
                <dt
                  dangerouslySetInnerHTML={{
                    __html: declineButtonCustomization.buttonText,
                  }}
                />
              </div>
            </button>
            {isLoading && !stayOnPage && (
              <div className="absolute inset-0 cursor-not-allowed flex justify-center items-center bg-white bg-opacity-90">
                <div className="h-9">
                  <LoadingSpinner />
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
};
